/**
 * Curated outbound state regulatory resources for RHC/FQHC (AB#10983 / AB#10969).
 * No scraping / no backend — hand-maintained links only.
 */

export type StateRegulatoryCatalogEntry = {
  id: string;
  stateCode: string; // "CA"
  stateName: string;
  title: string;
  topics: string[];
  source: "rhihub" | "source_on_healthcare";
  url: string;
  lastRefreshed: string; // ISO date "2026-09-07"
  facilityTypeSlugs: string[]; // include both RHC + FQHC slugs
};

const RHC_FQHC_SLUGS = [
  "rural_health_clinic",
  "federally_qualified_health_center",
];

export const STATE_REGULATORY_CATALOG: StateRegulatoryCatalogEntry[] = [
  {
    id: "rhihub-ca-overview",
    stateCode: "CA",
    stateName: "California",
    title: "Rural health for California (RHIhub)",
    topics: ["rural health", "RHC", "FQHC", "state overview"],
    source: "rhihub",
    url: "https://www.ruralhealthinfo.org/states/california",
    lastRefreshed: "2026-09-07",
    facilityTypeSlugs: [...RHC_FQHC_SLUGS],
  },
  {
    id: "rhihub-ca-resources",
    stateCode: "CA",
    stateName: "California",
    title: "California rural health resources (RHIhub)",
    topics: ["resources", "workforce", "facilities"],
    source: "rhihub",
    url: "https://www.ruralhealthinfo.org/states/california/resources",
    lastRefreshed: "2026-09-07",
    facilityTypeSlugs: [...RHC_FQHC_SLUGS],
  },
  {
    id: "rhihub-tx-overview",
    stateCode: "TX",
    stateName: "Texas",
    title: "Rural health for Texas (RHIhub)",
    topics: ["rural health", "RHC", "FQHC", "state overview"],
    source: "rhihub",
    url: "https://www.ruralhealthinfo.org/states/texas",
    lastRefreshed: "2026-09-07",
    facilityTypeSlugs: [...RHC_FQHC_SLUGS],
  },
  {
    id: "rhihub-tx-resources",
    stateCode: "TX",
    stateName: "Texas",
    title: "Texas rural health resources (RHIhub)",
    topics: ["resources", "workforce"],
    source: "rhihub",
    url: "https://www.ruralhealthinfo.org/states/texas/resources",
    lastRefreshed: "2026-09-07",
    facilityTypeSlugs: [...RHC_FQHC_SLUGS],
  },
  {
    id: "rhihub-ny-overview",
    stateCode: "NY",
    stateName: "New York",
    title: "Rural health for New York (RHIhub)",
    topics: ["rural health", "RHC", "FQHC", "state overview"],
    source: "rhihub",
    url: "https://www.ruralhealthinfo.org/states/new-york",
    lastRefreshed: "2026-09-07",
    facilityTypeSlugs: [...RHC_FQHC_SLUGS],
  },
  {
    id: "rhihub-fl-overview",
    stateCode: "FL",
    stateName: "Florida",
    title: "Rural health for Florida (RHIhub)",
    topics: ["rural health", "RHC", "FQHC", "state overview"],
    source: "rhihub",
    url: "https://www.ruralhealthinfo.org/states/florida",
    lastRefreshed: "2026-09-07",
    facilityTypeSlugs: [...RHC_FQHC_SLUGS],
  },
  {
    id: "source-state-action",
    stateCode: "CA",
    stateName: "California",
    title: "State action overview — Source on Healthcare Price & Competition",
    topics: ["state legislation", "competition", "price"],
    source: "source_on_healthcare",
    url: "https://sourceonhealthcare.org/state-action/",
    lastRefreshed: "2026-09-07",
    facilityTypeSlugs: [...RHC_FQHC_SLUGS],
  },
];

/** Return catalog rows for a state code (and optional facility slug). */
export function filterStateCatalog(
  stateCode: string | null,
  facilityTypeSlug?: string | null
): StateRegulatoryCatalogEntry[] {
  if (!stateCode) return [];
  const code = stateCode.toUpperCase();
  const slug = (facilityTypeSlug || "").trim().toLowerCase().replace(/-/g, "_");
  return STATE_REGULATORY_CATALOG.filter((row) => {
    if (row.stateCode.toUpperCase() !== code) return false;
    if (!slug) return true;
    const allowed = row.facilityTypeSlugs.map((s) =>
      s.toLowerCase().replace(/-/g, "_")
    );
    // Short aliases used in session / checkout
    if (slug === "rhc" && allowed.includes("rural_health_clinic")) return true;
    if (slug === "fqhc" && allowed.includes("federally_qualified_health_center")) {
      return true;
    }
    return (
      allowed.includes(slug) ||
      allowed.includes("rhc") ||
      allowed.includes("fqhc")
    );
  });
}
